import moment from 'moment';
import _get from 'lodash/get';
import _range from 'lodash/range';

import qs from 'qs';

import { handleActions } from 'redux-actions';
import { getFormValues, change } from 'redux-form';
import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';
import http from '../shared/http';
import createFilters from '../shared/fixed-filters';
import endpoints from '../../config/endpoints';
import { default as createCalendar } from '../shared/calendar';

import { regions } from '../../../../../enums/regions';
import { LOCATION_CHANGE } from 'react-router-redux';
import csvExport from '../shared/csvExport';

// actions types
const FETCH_DATA_SUCCESS = 'top-affiliates-list/fetch-data-success';
const FETCH_DATA_ERROR = 'top-affiliates-list/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'top-affiliates-list/toggle-progress-bar';
const TOGGLE_NOTE_POPUP = 'top-affiliates-list/TOGGLE_NOTE_POPUP';

const PAGINATE = 'top-affiliates-list/PAGINATE';
const DEFAULT_PAGINATION = 'top-affiliates-list/DEFAULT_PAGINATION';
const TOGGLE_PERIOD_SWITCH = 'top-affiliates-list/TOGGLE_PERIOD_SWITCH';

const FETCH_EXTENDED_DATA_SUCCESS = 'top-affiliates-list/FETCH_EXTENDED_DATA_SUCCESS';
const FETCH_NOTES_DATA_SUCCESS = 'top-affiliates-list/FETCH_NOTES_DATA_SUCCESS';
const UPDATE_NOTES_SUCCESS = 'top-affiliates-list/UPDATE_NOTES_SUCCESS';
const REMOVE_EXTENDED_DATA = 'top-affiliates-list/REMOVE_EXTENDED_DATA';
const SET_LOADING_AFFILIATE_ID = 'top-affiliates-list/SET_LOADING_AFFILIATE_ID';

const EMPTY_REDUCER_TYPE = 'top-affiliates-list/EMPTY_REDUCER_TYPE';

const FETCH_OK = 'top-affiliates-list/FETCH_OK';
const FETCH_NOT_OK = 'top-affiliates-list/FETCH_NOT_OK';

// permissions
export const allowedPermissions = {
	viewAll: 'top_affiliates_view_all',
	monthlyReportView: 'affiliate_monthly_report_view',
	canCopyTotalRow: 'can_copy_total_row',
	view: 'affiliates_list'
};

const calendar = createCalendar({
	namespace: 'topAffiliatesList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'topAffiliatesList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_IDS
			}
		},
		{
			name: 'admins',
			title: 'Manager name',
			placeholder: 'Manager name',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100&is_show_name=true'
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			searchable: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'traffic_source',
			title: 'Traffic source',
			placeholder: 'Select traffic source',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'static',
				source: 'root.lists.traffic-sources'
			}
		},
		{
			name: 'offer_geo',
			title: 'Offer geo',
			placeholder: 'Add offer geo',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			isOpenAfterSelection: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_GEO
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add affiliate\'s tag',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_TAGS,
				params: 'isHiddenWhenEmptySearch=true'
			}
		}
	]
});

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	periodSwitch: null,
	loadingAffiliateId: null,
	extendedData: [],
	expandedAffiliateIds: [],
	noteData: null,
	notePopupData: null,
	isNotePopupVisible: false,
	table: {
		columns: {
			affiliate_id: {
				index: 'affiliate_id',
				title: 'Affiliate ID',
				className: 'fixed-col bald-font text-center'
			},
			manager_name: {
				index: 'manager_name',
				title: 'Manager name',
				className: 'text-center'
			},
			affiliate_balance: {
				index: 'affiliate_balance',
				title: 'Affiliate balance',
				className: 'text-center'
			},
			forecast: {
				index: 'forecast',
				title: 'Forecast',
				className: 'text-center'
			}
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	filters: filters.initialState,
	calendar: calendar.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const {
				filters: { selectedFilters }, pagination, expandedAffiliateIds, periodSwitch
			} = getState().topAffiliatesList;
			const filters = {};
			const inverse_filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						!filter.shouldInverse ? (filters[filter.name] = values) : (inverse_filters[filter.name] = values) ;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;
			const leadsFrom = getFormValues('conversionsFromTopAffiliatesForm')(getState()) || {};

			const query = qs.stringify({
				filters,
				inverse_filters,
				period: periodSwitch,
				limit: pagination.perPage,
				offset: pagination.perPage * (pagination.currentPage - 1),
				leads_from: leadsFrom.conversionsFrom,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});
			dispatch({ type: TOGGLE_PROGRESS_BAR });

			return (http({
				url: `v2/top-affiliates?${query}`,
				method: 'get',
				actions: [ FETCH_DATA_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch)
				.then(async action => {
					if (action.type === FETCH_DATA_SUCCESS && expandedAffiliateIds[0]) {
						await dispatch(actions.fetchExtendedData());
					}

					Object.keys(leadsFrom)
						.forEach(key => dispatch(change('conversionsFromTopAffiliatesForm', key, leadsFrom[key])));

					return dispatch(actions.listNotes());
				});
		},
		fetchExtendedData: (affiliate_id) => (dispatch, getState) => {
			const {
				filters: { selectedFilters }, expandedAffiliateIds, periodSwitch
			} = getState().topAffiliatesList;

			const leadsFrom = getFormValues('conversionsFromTopAffiliatesForm')(getState()) || {};

			if (expandedAffiliateIds.includes(affiliate_id)) {
				return dispatch({ type: REMOVE_EXTENDED_DATA, payload: affiliate_id });
			}

			if (!expandedAffiliateIds[0] && !affiliate_id) {
				return;
			}

			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;

			const query = qs.stringify({
				filters,
				period: periodSwitch,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				expand_by_affiliate_ids: affiliate_id || expandedAffiliateIds,
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});

			if (affiliate_id) {
				dispatch({ type: SET_LOADING_AFFILIATE_ID, payload: affiliate_id });
			}

			return (http({
				url: `v2/top-affiliates/expanded?${query}`,
				method: 'get',
				actions: [ FETCH_EXTENDED_DATA_SUCCESS, FETCH_DATA_ERROR ],
				addToResponse: { affiliate_id: affiliate_id || null }
			}))(dispatch)
				.then(action => {
					if (action.type === FETCH_EXTENDED_DATA_SUCCESS && affiliate_id) {
						dispatch({ type: SET_LOADING_AFFILIATE_ID, payload: null });
					}
				});
		},
		listNotes: () => (dispatch, getState) => {
			const { start, end } = selectors.getCalendar(getState()).value;

			const query = qs.stringify({
				start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z`
			});

			return (http({
				url: `v2/top-affiliates/notes?${query}`,
				method: 'get',
				actions: [ FETCH_NOTES_DATA_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch);
		},
		toggleNotePopup: (data) => (dispatch, getState) => {

			if (!data || !data.row) {
				return dispatch({ type: TOGGLE_NOTE_POPUP });
			}

			const popupData = {
				affiliate_id: data.row.affiliate_id,
				country_code: data.row.country_code || null,
				offer_id: data.row.offer_id || null,
				date: data.item
			};

			dispatch({ type: TOGGLE_NOTE_POPUP, payload: popupData });
			dispatch(change('topAffiliateNoteForm', 'comment', ''));
		},
		submitNote: (data) => (dispatch, getState) => {

			if (!data) {
				return;
			}

			return (http({
				url: `v2/top-affiliates/notes`,
				method: 'post',
				data: data,
				actions: [ UPDATE_NOTES_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch)
				.then(action => {
					if (action.type === UPDATE_NOTES_SUCCESS) {
						dispatch(change('topAffiliateNoteForm', 'comment', ''));
						dispatch(actions.listNotes());
					}
				});
		},
		deleteNote: (id) => (dispatch, getState) => {

			return (http({
				url: `v2/top-affiliates/notes/${id}`,
				method: 'delete',
				actions: [ EMPTY_REDUCER_TYPE, FETCH_DATA_ERROR ]
			}))(dispatch)
				.then(action => {
					if (action.type === EMPTY_REDUCER_TYPE) {
						dispatch(actions.listNotes());
					}
				});
		},
		updateConversionFromCheckbox: (name = '') => (dispatch, getState) => {
			const formName = 'conversionsFromTopAffiliatesForm';

			if (name === 'is_from_cpa') {
				dispatch(change(formName, 'is_from_real_crm', false));
			}

			if (name === 'is_from_real_crm') {
				dispatch(change(formName, 'is_from_cpa', false));
			}
		},
		apply: () => dispatch => {
			dispatch({ type: DEFAULT_PAGINATION });
			dispatch(actions.fetchData());
		},
		downloadMonthlyRanksReport: (type = 'ranks') => (dispatch, getState) => {

			const { filters: { selectedFilters }, pagination, periodSwitch } = getState().topAffiliatesList;
			const filters = {};
			const inverse_filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						!filter.shouldInverse ? (filters[filter.name] = values) : (inverse_filters[filter.name] = values) ;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;
			const leadsFrom = getFormValues('conversionsFromTopAffiliatesForm')(getState()) || {};

			const query = qs.stringify({
				filters,
				inverse_filters,
				period: periodSwitch,
				limit: pagination.perPage,
				offset: pagination.perPage * (pagination.currentPage - 1),
				leads_from: leadsFrom.conversionsFrom,
				is_from_real_crm: !!leadsFrom.is_from_real_crm || '',
				is_from_cpa: !!leadsFrom.is_from_cpa  || '',
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});
			dispatch({ type: TOGGLE_PROGRESS_BAR });

			const url = type === 'ranks'
				? `v2/top-affiliates/monthly-ranks-report?${query}`
				: `v2/top-affiliates/monthly-report?${query}`;

			return (http({
				url,
				method: 'get',
				actions: [ FETCH_OK, FETCH_NOT_OK ]
			}))(dispatch)
				.then(action => {
					dispatch({ type: TOGGLE_PROGRESS_BAR });
					if (action.type === FETCH_OK) {
						const data = action.payload.data;

						const headers = Object.keys(data[0]);

						const columns = data.map(entity => headers.map(h => entity[h] || '-'));
						const rowsForExport = [].concat([ headers ], columns);

						csvExport(
							type === 'ranks' ? 'AffiliatesRanksReport' : 'AffiliatesMonthlyReport',
							rowsForExport
						);
					}
				});

		},
		togglePeriodSwitch: (type) => (dispatch) => {
			dispatch({ type: TOGGLE_PERIOD_SWITCH, payload: type || null });
			dispatch(actions.apply());
		},
		gotoPage: page => (dispatch, getState) => {
			dispatch({ type: PAGINATE, payload: page });
			dispatch(actions.fetchData());
		}
	},
	filters.actions,
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return {...initialState};
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.data,
			pagination: {
				...state.pagination,
				total: _get(action, 'payload.data.data[1].rows_count', 0)
			},
			intervals: action.payload.data.intervals,
			showProgressBar: false,
			error: false
		}),
		[FETCH_NOTES_DATA_SUCCESS]: (state, action) => ({
			...state,
			noteData: action.payload.data
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state, showProgressBar: !state.showProgressBar
		}),
		[TOGGLE_NOTE_POPUP]: (state, action) => ({
			...state,
			isNotePopupVisible: !state.isNotePopupVisible,
			notePopupData: action.payload ? action.payload : null
		}),
		[FETCH_EXTENDED_DATA_SUCCESS]: (state, action) => ({
			...state,
			extendedData: action.meta.affiliate_id
				? [...state.extendedData].concat(action.payload.data.data)
				: action.payload.data.data,
			expandedAffiliateIds: [...state.expandedAffiliateIds].concat(action.meta.affiliate_id || [])
		}),
		[TOGGLE_PERIOD_SWITCH]: (state, action) => ({
			...state, periodSwitch: action.payload
		}),
		[REMOVE_EXTENDED_DATA]: (state, action) => ({
			...state,
			extendedData: state.extendedData.filter(ed => action.payload !== ed['affiliate_id']),
			expandedAffiliateIds: state.expandedAffiliateIds.filter(eai => action.payload !== eai)
		}),
		[DEFAULT_PAGINATION]: (state, action) => ({ ...state, pagination: initialState.pagination }),
		[SET_LOADING_AFFILIATE_ID]: (state, action) => ({ ...state, loadingAffiliateId: action.payload }),
		[PAGINATE]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state, showProgressBar: false, error: true
		})
	},
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, {...initialState});

// selectors
export const selectors = {
	getData: state => state.topAffiliatesList.data,
	getNoteData: state => state.topAffiliatesList.noteData,
	getNotePopupData: state => state.topAffiliatesList.notePopupData,
	getIsNotePopupVisible: state => state.topAffiliatesList.isNotePopupVisible,
	getExpandedAffiliateIds: state => state.topAffiliatesList.expandedAffiliateIds,
	getIntl: state => intl(state),
	getTable: state => {
		const { table, data, intervals, extendedData } = state.topAffiliatesList;
		let rows = data || [];

		if (extendedData && extendedData.length) {
			rows = rows.reduce((acc, row) => {
				const expandedRows = extendedData.filter(item => item.affiliate_id === row.affiliate_id);

				return [...acc]
					.concat(row)
					.concat(expandedRows);
			}, []);
		}

		return {
			headers: Object.values(table.columns),
			rows: rows.filter(Boolean),
			intervals: intervals && intervals.map(item => item.date).sort(() => -1),
			totalRow: rows[0] && !rows[0].affiliate_id ? rows[0] : null,
			rows_count: rows[1] && rows[1].rows_count
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.topAffiliatesList.filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.topAffiliatesList.calendar, state => state
	)(state),
	getPagination: state => {
		const { perPage, total, currentPage } = state.topAffiliatesList.pagination;
		return {
			...state.topAffiliatesList.pagination,
			pages: _range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getAllowedToCopyWords: (state) => {
		const { rows } = selectors.getTable(state);
		const notePopupAffiliateId = selectors.getNotePopupData(state) && selectors.getNotePopupData(state).affiliate_id;
		const noteData = selectors.getNoteData(state) || [];

		return []
			.concat(rows.map(row => row.affiliate).filter(Boolean))
			.concat(rows.map(row => row.offer_geo).filter(Boolean))
			.concat(rows.map(row => row.manager).filter(Boolean))
			.concat(
				noteData
					.filter(item => item.affiliate_id === notePopupAffiliateId)
					.map(row => row.comment)
					.filter(Boolean)
			)
			.concat(rows.map(row => row.affiliate_id).filter(Boolean));
	},
	getProgressBar: state => state.topAffiliatesList.showProgressBar,
	getIsHiddenProfit: state => {
		const leadsFrom = getFormValues('conversionsFromTopAffiliatesForm')(state) || {};

		return !!leadsFrom.is_hidden_profit;
	},
	getLoadingAffiliateId: state => state.topAffiliatesList.loadingAffiliateId,
	getPeriodSwitch: state => state.topAffiliatesList.periodSwitch,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.topAffiliatesList.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/top-affiliates/index.js